import { queryOptions, useQuery } from '@tanstack/react-query'
import { loadSourceData, loadGlonassoftData } from '@/shared/api/sourceApi'
import { SourceAccount } from '@/app/types/source'
import { SYSTEM_TYPE } from '@/app/store/systemStore'
import { getSessionAbortSignal } from './sessionAbortController'

export interface AgentsQueryCredentials {
  login: string
  password: string
}

interface AgentsQueryParams {
  systemType: SYSTEM_TYPE
  credentials: AgentsQueryCredentials | null
  sourceApiUrl: string
  systemUrl: string
}

export type AgentsErrorReason =
  | 'invalid_credentials'
  | 'no_response'
  | 'unknown'

export interface AgentsQueryResult {
  data: SourceAccount[]
  error?: string
  reason?: AgentsErrorReason
}

// Образцы текста ошибки, которые на практике соответствуют неверному логину/паролю
// для конкретной системы (HTTP-статус у всех систем одинаковый — 500 — поэтому
// различать причину можно только по тексту).
const INVALID_CREDENTIALS_SIGNATURE: Record<SYSTEM_TYPE, string | null> = {
  [SYSTEM_TYPE.OMNICOMM]: '404 (Not Found)',
  [SYSTEM_TYPE.AXENTA]: '400 (Bad Request)',
  [SYSTEM_TYPE.GELIOS]: '404 (Not Found)',
  [SYSTEM_TYPE.FORTMONITOR]: 'Ответ не содержит заголовков авторизации',
  [SYSTEM_TYPE.GLONASSOFT]: 'Неверное имя/пароль',
  [SYSTEM_TYPE.WIALON]: null,
}

// Некоторые системы (например FortMonitor) возвращают разный текст для одной
// и той же причины — на этот случай ловим явное упоминание логина/пароля
// независимо от точной формулировки конкретной системы.
const GENERIC_INVALID_CREDENTIALS_KEYWORDS = ['логин', 'пароль', 'парол']

const classifyAgentsError = (
  systemType: SYSTEM_TYPE,
  status: number | undefined,
  rawMessage: string | undefined
): AgentsErrorReason => {
  if (status === undefined) return 'no_response'
  if (!rawMessage) return 'unknown'

  const signature = INVALID_CREDENTIALS_SIGNATURE[systemType]
  if (signature && rawMessage.includes(signature)) {
    return 'invalid_credentials'
  }

  const lowerMessage = rawMessage.toLowerCase()
  if (
    GENERIC_INVALID_CREDENTIALS_KEYWORDS.some((keyword) =>
      lowerMessage.includes(keyword)
    )
  ) {
    return 'invalid_credentials'
  }

  return 'unknown'
}

export const agentsQueryOptions = ({
  systemType,
  credentials,
  sourceApiUrl,
  systemUrl,
}: AgentsQueryParams) =>
  queryOptions({
    queryKey: ['agents', systemType, credentials] as const,
    queryFn: async (): Promise<AgentsQueryResult> => {
      const result =
        systemType === SYSTEM_TYPE.GLONASSOFT
          ? await loadGlonassoftData<SourceAccount>(
              'Agents',
              credentials!,
              sourceApiUrl,
              systemUrl,
              getSessionAbortSignal()
            )
          : await loadSourceData<SourceAccount>(
              'Agents',
              systemType === SYSTEM_TYPE.AXENTA
                ? encodeURIComponent(JSON.stringify(credentials))
                : JSON.stringify(credentials),
              sourceApiUrl,
              systemUrl,
              systemType,
              getSessionAbortSignal()
            )

      if (!result.error) {
        return { data: result.data }
      }

      return {
        data: result.data,
        error: result.error,
        reason: classifyAgentsError(
          systemType,
          result.status,
          result.rawMessage
        ),
      }
    },
    enabled: !!credentials,
    staleTime: 5 * 60 * 1000,
  })

export const useAgentsQuery = (params: AgentsQueryParams) =>
  useQuery(agentsQueryOptions(params))
